import {Locale} from './locale';
import {Program} from './program';
import {TemplateComponentContent} from './template-component-content';
import {TemplateComponent} from './template-component';
import {BulkEditChangeTypeEnum} from './bulk-edit-change-type.enum';
import {DeliveryPath} from './delivery-path.enum';
import {factory} from '../shared/app-logging';
import {Product} from './product';

const logger = factory.getLogger('BulkEditChangeRecord');

export class BulkEditChangeRecord {
  public currentText: string;
  public currentContent: TemplateComponentContent;
  public newContent: TemplateComponentContent;
  public type: BulkEditChangeTypeEnum;
  public selected: boolean;
  public persisted: boolean;
  public error: Error;
  public result: TemplateComponentContent | Error;

  public static isObsoleteOldContent(ber: BulkEditChangeRecord): boolean {
    const thereIsNotOldContent = typeof ber.oldText !== 'string';
    const currentContentIsNotDefault = ber.type !== BulkEditChangeTypeEnum.ADD;
    const currentDiffersFromOld = ber.currentText !== ber.oldText;
    return thereIsNotOldContent || (currentContentIsNotDefault && currentDiffersFromOld);
  }

  public static getCurrentContent(component: TemplateComponent, locale: Locale, product: Product): TemplateComponentContent {
    let result: TemplateComponentContent = null;
    if (component && component.contents) {
      result = component.contents.find(cont => cont.locale === locale.localeName && cont.productId === product.id);
      if (!result) {
        result = component.contents.find(cont => cont.locale === locale.localeName);
        result = result ? result : component.contents[0];
      }
    }
    return result;
  }

  public static getChangeType(currentContent: TemplateComponentContent, locale: Locale, product: Product, newText: string): BulkEditChangeTypeEnum {
    const sameCombination = currentContent.locale === locale.localeName &&
      (product.id === currentContent.productId ||
        (!currentContent.productId && (!product.id || product.id === 'DEFAULT')));
    const currentText = currentContent.content ? currentContent.content.replace(/\r/g, '') : '';
    const currentLength = currentText.length;
    const newLength = newText.length;
    // const currentLength = currentText.trim().length;
    // const newLength = newText.trim().length;

    if (sameCombination) {
      if (currentLength > 0) {
        if (newLength === 0) {
          return BulkEditChangeTypeEnum.REVERT;
        } else if (currentText !== newText) {
          return BulkEditChangeTypeEnum.MODIFY;
        }
      }
    } else if (newLength > 0) {
      return BulkEditChangeTypeEnum.ADD;
    }

    return BulkEditChangeTypeEnum.UNCHANGED;
  }

  public static getNewContentByCurrentAndChange(component: TemplateComponent,
                                                current: TemplateComponentContent,
                                                change: BulkEditChangeTypeEnum,
                                                locale: Locale,
                                                product: Product,
                                                newText: string): TemplateComponentContent {
    let result: TemplateComponentContent = null;
    if (component && component.contents) {
      switch (change) {
        case BulkEditChangeTypeEnum.UNCHANGED:
          result = current;
          break;
        case BulkEditChangeTypeEnum.ADD:
          result = current.clone();
          result.id = null;
          result.isDefaultInd = false;
          result.productId = product.id;
          result.locale = locale.localeName;
          result.content = newText;
          break;
        case BulkEditChangeTypeEnum.MODIFY:
          result = current.clone();
          result.content = newText;
          break;
        case BulkEditChangeTypeEnum.REVERT:
          if (!product || !product.id || product.id.trim() !== 'DEFAULT') {
            result = component.contents.find(cont => cont.locale !== locale.localeName);
          } else {
            result = component.contents.find(cont => cont.locale === locale.localeName && cont.productId !== product.id);
          }
          result = result ? result : component.contents[0];
          break;
        default:
          result = current;
          break;
      }
    }
    return result;
  }

  public static getNewContent(component: TemplateComponent, locale: Locale, product: Product, newText: string): TemplateComponentContent {
    const current = BulkEditChangeRecord.getCurrentContent(component, locale, product);
    const type = BulkEditChangeRecord.getChangeType(current, locale, product, newText);
    return BulkEditChangeRecord.getNewContentByCurrentAndChange(component, current, type, locale, product, newText);
  }

  constructor(
    public currentComponent: TemplateComponent,
    public locale: Locale,
    public product: Product,
    public deliveryPath: DeliveryPath,
    public newText: string,
    public oldText: string
  ) {
    if (currentComponent && currentComponent.contents) {
      this.currentContent = BulkEditChangeRecord.getCurrentContent(currentComponent, locale, product);
      this.currentText = this.currentContent.content.replace(/\r/g, '');
      this.newText = newText ? newText.replace(/\r/g, '') : '';
      this.oldText = oldText ? oldText.replace(/\r/g, '') : oldText;
      this.type = BulkEditChangeRecord.getChangeType(this.currentContent, locale, product, this.newText);
      this.newContent = BulkEditChangeRecord.getNewContentByCurrentAndChange(currentComponent,
        this.currentContent,
        this.type,
        this.locale,
        this.product,
        this.newText);
      this.selected = true;
      this.persisted = false;
      this.error = undefined;
    } else {
      this.error = new Error('The component to be customized was not provided');
    }
  }

  public isObsoleteOldContent() {
    return BulkEditChangeRecord.isObsoleteOldContent(this);
  }

}
